import { useCallback, useRef } from 'react';

import { CloseIcon, LightningIcon } from '@braskai/icons';
import { Button, Modal, Typography } from '@braskai/ui-kit';
import { useNavigate } from 'react-router-dom';

import { Box } from '@mui/material';

import { useCurrentUser } from '@/entities/user/currentUser';

import { FeaturedUpgradeIcon } from '@/shared/assets/icons/FeaturedUpgradeIcon';
import { PATHS } from '@/shared/constants/routes/paths';
import { AppLocale, useInterfaceLanguage } from '@/shared/i18n';
import { formatInterfaceDate } from '@/shared/i18n/formatLocale';
import { readCheckoutEntryPoint } from '@/shared/lib/checkoutEntryPoint';
import { markRecordingCheckoutReturnPaid } from '@/shared/lib/recordingCheckoutReturn';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { BoosterType, PaymentInterval, Price } from '@/shared/types/Billing';

import { usePlanChangeConfirmation } from '../../../model/usePlanChangeConfirmation';
import { useUpgradeConfirmationModalStore } from '../../model/useUpgradeConfirmationModalStore';
import {
  SActions,
  SBoosterContent,
  SBoosterIncludedCard,
  SCloseButton,
  SContent,
  SHeader,
  SHeaderTop,
  SModalContainer,
  SNewPlanSection,
  SPlanSection,
} from './styled';

const getDowngradeDate = (targetPrice: Price, locale: AppLocale) => {
  const currentDate = new Date();
  const isYearlyPlan = targetPrice.recurring?.interval === PaymentInterval.Year;

  if (isYearlyPlan) {
    currentDate.setFullYear(currentDate.getFullYear() + 1);
  } else {
    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  return formatInterfaceDate(locale, currentDate);
};

export const UpgradeConfirmationModal = () => {
  const isOpen = useUpgradeConfirmationModalStore((state) => state.isOpen);
  const close = useUpgradeConfirmationModalStore((state) => state.close);
  const currentPlan = useUpgradeConfirmationModalStore((state) => state.currentPlan);
  const targetPlan = useUpgradeConfirmationModalStore((state) => state.targetPlan);
  const targetPrice = useUpgradeConfirmationModalStore((state) => state.targetPrice);
  const minutesToAdd = useUpgradeConfirmationModalStore((state) => state.minutesToAdd);

  const { data: user } = useCurrentUser();
  const { locale, t } = useInterfaceLanguage();
  const navigate = useNavigate();
  // The recording whose paywall started this upgrade, once the upgrade is paid: closing the result goes back to it
  // (and it repeats the blocked action) instead of Home. Kept here because the recording may already have used up
  // the stored return by the time the pending payment's modal is closed.
  const paidRecordingPathRef = useRef<null | string>(null);
  const handleClosedAfterProcessing = useCallback(() => {
    if (paidRecordingPathRef.current) {
      navigate(paidRecordingPathRef.current, { replace: true });
      return;
    }

    navigate(PATHS.homepage);
  }, [navigate]);

  const openPlanChangeConfirmation = usePlanChangeConfirmation({
    onClosedAfterProcessing: handleClosedAfterProcessing,
  });

  const handleUpgrade = () => {
    if (!targetPrice?.id || !targetPlan?.id) return;

    MixpanelEvents.upgradeNowClick({
      currentPlan: currentPlan?.name ?? '',
      newPlan: targetPlan.name,
    });
    close();
    paidRecordingPathRef.current = null;

    openPlanChangeConfirmation({
      confirmButtonText: t('pricing.change.upgradeNow'),
      errorMessage: t('pricing.change.upgradeError'),
      planId: targetPlan.id,
      priceId: targetPrice.id,
      successDescriptions: {
        noPayment: t('pricing.change.upgradeSuccess'),
        paid: t('pricing.change.upgradePaidSuccess'),
      },
      onConfirmAnalytics: () =>
        MixpanelEvents.upgradeSubscribeConfirmClick({
          currentPlan: currentPlan?.name ?? '',
          newPlan: targetPlan.name,
        }),
      // An upgrade chosen on a pricing page that a recording paywall opened returns to that recording.
      onPaid: () => {
        paidRecordingPathRef.current = markRecordingCheckoutReturnPaid(readCheckoutEntryPoint())?.path ?? null;
      },
    });
  };

  const handleCancel = () => {
    close();
  };

  if (!currentPlan || !targetPlan || !targetPrice) {
    return null;
  }

  const currentIntervalLabel = t(
    user?.subscription?.plan.interval === PaymentInterval.Year ? 'pricing.change.yearly' : 'pricing.change.monthly',
  );
  const targetIntervalLabel = t(
    targetPrice.recurring?.interval === PaymentInterval.Year ? 'pricing.change.yearly' : 'pricing.change.monthly',
  );

  return (
    <Modal open={isOpen} onClose={handleCancel}>
      <SModalContainer>
        {/* Header */}
        <SHeader>
          <SHeaderTop>
            <FeaturedUpgradeIcon height={56} width={56} />
            <SCloseButton data-testid="upgrade-confirmation-modal-cancel" onClick={handleCancel}>
              <CloseIcon height={24} width={24} />
            </SCloseButton>
          </SHeaderTop>
        </SHeader>

        {/* Content */}
        <SContent>
          {/* Current Plan */}
          <SPlanSection>
            <Typography color="grey.900" variant="body-1-md">
              {t('pricing.change.current')}
            </Typography>
            {user?.subscription?.current_period_end && (
              <Typography color="grey.700" variant="body-2">
                {t('pricing.change.currentDescription', {
                  date: formatInterfaceDate(locale, user.subscription.current_period_end),
                  interval: currentIntervalLabel,
                  plan: currentPlan.name,
                })}
              </Typography>
            )}
          </SPlanSection>

          {/* New Plan */}
          <SNewPlanSection>
            <SPlanSection>
              <Typography color="primary.600" variant="body-1-md">
                {t('pricing.change.new')}
              </Typography>
              <Typography color="grey.700" variant="body-2">
                {t('pricing.change.upgradeDescription', {
                  count: minutesToAdd || 0,
                  interval: targetIntervalLabel,
                  plan: targetPlan.name,
                })}
              </Typography>
            </SPlanSection>

            {/* Booster Included Card - shown conditionally */}
            {targetPlan.boosters_to_remove?.includes(BoosterType.SRT) && (
              <SBoosterIncludedCard>
                <Box color="primary.500">
                  <LightningIcon color="primary.500" height={24} width={24} />
                </Box>

                <SBoosterContent>
                  <Typography color="grey.900" variant="body-2-md">
                    {t('pricing.change.boosterIncluded')}
                  </Typography>
                  <Typography color="grey.700" variant="body-2">
                    {t('pricing.change.boosterDescription')}
                  </Typography>
                </SBoosterContent>
              </SBoosterIncludedCard>
            )}
          </SNewPlanSection>
        </SContent>

        {/* Actions */}
        <SActions>
          <Button data-testid="upgrade-confirmation-modal-upgrade-now" size="lg" fullWidth onClick={handleUpgrade}>
            {t('pricing.change.upgradeNow')}
          </Button>
          <Typography color="grey.500" sx={{ textAlign: 'center' }} variant="caption-1">
            {t('pricing.change.switchBack', { date: getDowngradeDate(targetPrice, locale) })}
          </Typography>
        </SActions>
      </SModalContainer>
    </Modal>
  );
};
